import type { ILoginService } from '../login.service';
import type { IToasterService } from 'ngtoaster';
import type { IController, IPromise, IQService, ISCEService, IScope, IWindowService, translate } from 'angular';
import type { ILoginStatus, LoginSteps } from './loginStatus.controller';
import type { EmailStatusType, PasswordType } from '../login.types';
import type { ICaptchaService } from '../../../_common/captcha/captcha.service';
import { Captcha, isCaptchaCancelled } from '../../../_common/captcha/captcha.types';
import { IEmailService } from '../../services/email/email.service';
import { IOnSendCodeResponse } from '@/scripts/_common/code-input/codeInput.types';
import LoginStatusController from './loginStatus.controller';
export interface ILoginEmailController extends IController, ILoginStatus<EmailStatusType> {
  email?: string;
  password?: string;
  inputPassword: PasswordType;
  lpId?: number;
  isNeedRegistered?: boolean;
  emailWatcher?: () => void;
  sendEmail(): void;
  switchPasswordType(): void;
  login(): void;
  recoveryPassword(): void;
  sendCode(): IPromise<IOnSendCodeResponse>;
  confirmCode(code: string): IPromise<unknown> | void;
}
export default class LoginEmailController extends LoginStatusController<EmailStatusType> implements ILoginEmailController {
  email?: string;
  password?: string;
  status: EmailStatusType = 'email';
  inputPassword: PasswordType = 'password';
  lpId?: number;
  isNeedRegistered?: boolean;
  emailWatcher?: () => void;
  protected readonly steps: LoginSteps<EmailStatusType> = {
    email: {
      title: () => this.$translate.instant('Js.Login.Email.EmailTitle'),
      description: () => this.$translate.instant('Js.Login.Email.EmailDescription'),
      showAuthMethods: true
    },
    password: {
      title: () => this.$translate.instant('Js.Login.Email.PasswordTitle'),
      description: () => this.$translate.instant('Js.Login.Email.PasswordDescription'),
      back: 'email'
    },
    confirmation: {
      title: () => this.$translate.instant('Js.Login.Email.ConfirmationTitle'),
      description: () => this.$translate.instant('Js.Login.Email.ConfirmationDescription', {
        email: this.email
      }),
      back: 'email'
    },
    registration: {
      title: () => this.$translate.instant('Js.Login.Email.RegistrationTitle'),
      description: () => this.$translate.instant('Js.Login.Email.RegistrationDescription'),
      back: 'email'
    },
    forgotPassword: {
      title: () => this.$translate.instant('Js.Login.Email.ForgotPasswordTitle'),
      description: () => this.$translate.instant('Js.Login.Email.ForgotPasswordDescription'),
      back: 'password'
    }
  };
  protected readonly backEventName = 'backLoginEmail';
  protected readonly returnErrorKey = 'Js.Login.Email.ReturnError';
  /* @ngInject */
  constructor(readonly loginService: ILoginService, toaster: IToasterService, readonly $window: IWindowService, $scope: IScope, readonly $sce: ISCEService, $translate: translate.ITranslateService, readonly captchaService: ICaptchaService, readonly emailService: IEmailService, readonly emailCodeLength: number, readonly $q: IQService) {
    super($scope, toaster, $translate);
  }
  $onInit() {
    this.initEmail();
    super.$onInit();
  }
  $onDestroy() {
    if (this.emailWatcher != null) {
      this.emailWatcher();
    }
  }
  sendEmail() {
    if (this.email == null) {
      this.toaster.pop('error', '', this.$translate.instant('Js.Login.Email.EmailEmptyError'));
      return;
    }
    let captcha: Captcha | null = null;
    const email = this.email;
    this.captchaService.confirmIfExist('auth').then(captchaResult => {
      captcha = captchaResult;
      return this.loginService.authorizationData({
        data: email,
        captchaId: captcha?.captchaId,
        inputValue: captcha?.inputValue,
        captchaInstanceId: captcha?.instanceId
      });
    }).then(authorizationStatus => {
      this.isNeedRegistered = authorizationStatus[0] !== 'y';
      if (authorizationStatus[1] === 'c') {
        this.changeStatus('confirmation');
      } else if (this.isNeedRegistered) {
        this.changeStatus('registration');
      } else {
        this.changeStatus('password');
      }
    }).catch(error => {
      if (!isCaptchaCancelled(error)) {
        this.toaster.pop('error', '', error.message);
      }
    }).finally(() => {
      if (captcha !== null) {
        captcha.close();
      }
    });
  }
  switchPasswordType() {
    this.inputPassword = this.inputPassword === 'password' ? 'text' : 'password';
  }
  login() {
    if (typeof this.email === 'undefined' || this.email === null) {
      this.toaster.pop('error', '', this.$translate.instant('Js.Login.Email.EmailEmptyError'));
      return;
    }
    if (typeof this.password === 'undefined' || this.password === null) {
      this.toaster.pop('error', '', this.$translate.instant('Js.Login.Email.PasswordEmptyError'));
      return;
    }
    let captcha: Captcha | null = null;
    const email = this.email;
    const password = this.password;
    this.captchaService.confirmIfExist('auth').then(captchaResult => {
      captcha = captchaResult;
      return this.loginService.emailLogin({
        email,
        password,
        captchaId: captcha?.captchaId,
        inputValue: captcha?.inputValue,
        captchaInstanceId: captcha?.instanceId
      });
    }).then(twoFactorRedirect => {
      if (twoFactorRedirect !== null && twoFactorRedirect !== '') {
        this.$window.location.assign(twoFactorRedirect);
      } else if (typeof this.redirectTo !== 'undefined' && this.redirectTo !== null && this.redirectTo !== '') {
        this.$window.location.assign(this.redirectTo);
      } else {
        this.$window.location.reload();
      }
    }).catch(error => {
      if (isCaptchaCancelled(error)) {
        return;
      }
      this.toaster.pop('error', '', error.message);
      this.password = undefined;
    }).finally(() => {
      if (captcha !== null) {
        captcha.close();
      }
    });
  }
  recoveryPassword() {
    if (typeof this.email === 'undefined' || this.email === null) {
      this.toaster.pop('error', '', this.$translate.instant('Js.Login.Email.EmailEmptyError'));
      return;
    }
    this.loginService.recoveryPassword(this.email, this.lpId).then(() => {
      this.changeStatus('forgotPassword');
    }).catch(error => {
      this.toaster.pop('error', '', error.message);
    });
  }
  sendCode() {
    if (typeof this.email === 'undefined' || this.email === null) {
      return this.$q.resolve({
        success: false
      });
    }
    const email = this.email;
    let captcha: Captcha | null = null;
    return this.captchaService.confirmIfExist('auth').then(captchaResult => {
      captcha = captchaResult;
      return this.emailService.sendCode({
        email,
        authorize: this.isNeedRegistered !== true,
        captchaId: captcha?.captchaId,
        inputValue: captcha?.inputValue,
        captchaInstanceId: captcha?.instanceId
      });
    }).then(() => {
      this.toaster.pop('success', '', this.$translate.instant('Js.Login.Email.SendCode.Success'));
      return {
        success: true
      };
    }).catch(error => {
      if (!isCaptchaCancelled(error)) {
        this.toaster.pop('error', '', this.$translate.instant('Js.Login.Email.SendCode.Error'));
      }
      return {
        success: false
      };
    }).finally(() => {
      if (captcha != null) {
        captcha.close();
      }
    });
  }
  confirmCode(code: string) {
    if (typeof this.email === 'undefined' || this.email === null || typeof code === 'undefined' || code === null) {
      return;
    }
    let captcha: Captcha | null = null;
    const email = this.email;
    return this.captchaService.confirmIfExist('auth').then(captchaResult => {
      captcha = captchaResult;
      return this.emailService.confirmCode({
        email,
        code,
        authorize: this.isNeedRegistered !== true,
        captchaId: captcha?.captchaId,
        inputValue: captcha?.inputValue,
        captchaInstanceId: captcha?.instanceId
      });
    }).then(() => {
      if (this.isNeedRegistered) {
        this.changeStatus('registration');
      } else if (typeof this.redirectTo !== 'undefined' && this.redirectTo !== null && this.redirectTo !== '') {
        this.$window.location.assign(this.redirectTo);
      } else {
        this.$window.location.reload();
      }
    }).catch(error => {
      if (!isCaptchaCancelled(error)) {
        this.toaster.pop('error', '', error.message);
      }
    }).finally(() => {
      if (captcha !== null) {
        captcha.close();
      }
    });
  }
  private initEmail() {
    if (this.parentCtrl != null) {
      this.email = this.parentCtrl.email;
    }
    this.emailWatcher = this.$scope.$watch('$ctrl.email', (newValue, _oldValue) => {
      if (this.parentCtrl != null && typeof newValue === 'string') {
        this.parentCtrl.email = newValue;
      }
    });
  }
}
LoginEmailController.$inject = ["loginService", "toaster", "$window", "$scope", "$sce", "$translate", "captchaService", "emailService", "emailCodeLength", "$q"];